/*
 * Kivora sticky Add to cart bar (sections/kv-sticky-atc.liquid).
 * - Watches the main product's Add to cart button; the bar shows while that button is above the viewport.
 * - Mirrors the buy box: the bundle picker's selected tier total and title (kv-bundle-picker) or the price.
 * - The button clicks the real Add to cart (so kv-bundle.js and Dawn handle colours and bundles), or
 *   scrolls back to the buy box.
 */
(() => {
  if (customElements.get('kv-sticky-atc')) return;

  class KvStickyAtc extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('product-info[id^="MainProduct-"]');
      if (!this.main) return;
      this.sectionId = this.main.dataset.section;
      this.button = this.querySelector('[data-kvsa-btn]');
      this.price = this.querySelector('[data-kvsa-price]');
      this.offer = this.querySelector('[data-kvsa-offer]');
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', this.onClick);

      this.bundle = this.main.querySelector('kv-bundle-picker');
      this.watchTarget();
      this.sync();

      if (this.bundle && 'MutationObserver' in window) {
        this.bundleObserver = new MutationObserver(this.sync);
        this.bundleObserver.observe(this.bundle, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          window.setTimeout(() => {
            this.watchTarget();
            this.sync();
          }, 80);
        });
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      if (this.bundleObserver) this.bundleObserver.disconnect();
      if (this.unsubscribe) this.unsubscribe();
    }

    atc() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    watchTarget() {
      const target = this.atc();
      if (!target || target === this.watched || !('IntersectionObserver' in window)) return;
      if (this.observer) this.observer.disconnect();
      this.watched = target;
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        // Visible only once the shopper has scrolled past the button (it sits above the viewport).
        this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(target);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.button.tabIndex = visible ? 0 : -1;
    }

    sync() {
      const tier = this.bundle && this.bundle.querySelector('.kvbp__tier.is-selected');
      if (tier) {
        const total = tier.querySelector('.kvbp__now');
        const title = tier.querySelector('.kvbp__title');
        if (total && this.price) this.price.textContent = total.textContent.trim();
        if (this.offer) {
          const multi = tier.dataset.kvbpTier !== '1';
          this.offer.hidden = !(multi && title);
          if (multi && title) this.offer.textContent = title.textContent.trim();
        }
        return;
      }
      const price = this.main.querySelector(`#price-${this.sectionId}`);
      if (price && this.price) {
        // The Shelf Tag price prints the amount twice (screen-reader text + split visual digits): keep the first.
        const text = price.textContent.replace(/\s+/g, ' ');
        const match = text.match(/[^\d\s]{0,3}\s?\d[\d,]*(?:\s?[.,]\s?\d{2})?/);
        if (match) this.price.textContent = match[0].replace(/\s/g, '');
      }
    }

    onClick() {
      const target = this.atc();
      if (this.dataset.action === 'scroll' || !target || target.disabled || target.getAttribute('aria-disabled') === 'true') {
        const anchor = this.main.querySelector('kv-bundle-picker') || this.main.querySelector('variant-selects') || target;
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      target.click();
    }
  }

  customElements.define('kv-sticky-atc', KvStickyAtc);
})();
